import React, { useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  HelpOutline as HelpIcon,
  PhotoCamera as CameraIcon,
  MenuBook as BookIcon,
  Restaurant as RestaurantIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { BookContainer, BookPage, BookNavigation } from '../components';
import { PAGE_NUMBERS } from '../utils/pageNumbers';
import { SEO } from '../utils/seo/SEO';

const HelpScreen: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>('getting-started');

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <HelpIcon sx={{ color: '#FF6B35', mr: 1 }} />,
      items: [
        {
          question: 'How do I create an account?',
          answer: 'Click "Sign Up" on the login screen. Enter your email, create a password, and choose a display name. Your account will be created instantly and you can start adding recipes right away.',
        },
        {
          question: 'What are Green Tokens?',
          answer: 'Green Tokens are the currency for AI features like recipe extraction, image generation, and voice instructions. Each feature shows its token cost. Check your balance on the home screen and buy more tokens anytime through the "Buy More" button.',
        },
        {
          question: 'How do I capture my first recipe?',
          answer: 'Tap "Capture Food" from the home screen. Take a clear photo of your recipe from a cookbook, magazine, or handwritten card. Tap "Extract from Photo" and wait for Otto to process it. Review the extracted content, make any edits, and save to your library.',
        },
      ],
    },
    {
      id: 'capturing',
      title: 'Capturing Recipes',
      icon: <CameraIcon sx={{ color: '#FF8A65', mr: 1 }} />,
      items: [
        {
          question: 'What makes a good recipe photo?',
          answer: 'Use good lighting, ensure the text is clear and readable, avoid shadows or glare, and capture the entire recipe. Hold your phone steady and make sure the recipe fills most of the frame.',
        },
        {
          question: 'Can I enter recipes manually?',
          answer: 'Yes! On the Capture Food screen, you can type in the recipe title, ingredients, and instructions manually without taking a photo. This is perfect for recipes you want to create from scratch.',
        },
        {
          question: 'How do I edit extracted recipes?',
          answer: 'After extraction, review all fields before saving. You can edit the title, ingredients list, and instructions. If Otto makes a mistake, just correct it directly in the text fields.',
        },
      ],
    },
    {
      id: 'organizing',
      title: 'Organizing Recipes',
      icon: <BookIcon sx={{ color: '#4A7C59', mr: 1 }} />,
      items: [
        {
          question: 'How do I search for recipes?',
          answer: 'On the Recipe List screen, use the search bar at the top to find recipes by name, ingredient, or keyword. The search updates in real-time as you type.',
        },
        {
          question: 'How do I favorite recipes?',
          answer: 'Tap the star icon on any recipe card to mark it as a favorite. You can filter to show only favorites using the favorites toggle on the Recipe List screen.',
        },
        {
          question: 'Can I delete recipes?',
          answer: 'Yes! Open any recipe, tap the Edit button, then scroll down and tap "Delete Recipe". You\'ll be asked to confirm before the recipe is permanently removed.',
        },
      ],
    },
    {
      id: 'cooking',
      title: 'Cooking Features',
      icon: <RestaurantIcon sx={{ color: '#6B9B7B', mr: 1 }} />,
      items: [
        {
          question: 'How do I use step-by-step mode?',
          answer: 'Open a recipe and tap "Start Cooking" to enter step-by-step mode. Navigate through instructions one at a time, set timers for each step, and ask Otto questions if you get stuck.',
        },
        {
          question: 'How do I scale recipes?',
          answer: 'Open a recipe and ask Otto to scale it by tapping "Ask Otto for Help". Request the number of servings you need (e.g., "scale this to 8 servings") and all measurements will update automatically.',
        },
        {
          question: 'Can Otto suggest substitutions?',
          answer: 'Absolutely! If you\'re missing an ingredient, ask Otto for substitutions. Open the recipe, tap "Ask Otto for Help", and ask something like "what can I use instead of buttermilk?" Otto will suggest alternatives.',
        },
      ],
    },
    {
      id: 'settings',
      title: 'Account & Settings',
      icon: <SettingsIcon sx={{ color: '#8D7B68', mr: 1 }} />,
      items: [
        {
          question: 'What AI models can I choose?',
          answer: 'In Settings, you can select different AI models for text processing, image generation, and voice output. Different models offer varying quality, speed, and token costs.',
        },
        {
          question: 'How do voice instructions work?',
          answer: 'Enable "Audio Generation" in Settings. When recipes are created or viewed, Otto can generate voice instructions. Choose your preferred voice type (alloy, echo, fable, etc.) in the voice settings.',
        },
        {
          question: 'How do I manage my tokens?',
          answer: 'View your token balance on the home screen. Purchase more tokens through the "Buy More" button which takes you to a secure Stripe payment page. Monitor your spending in Settings.',
        },
      ],
    },
  ];

  const filteredCategories = helpCategories
    .map(category => ({
      ...category,
      items: category.items.filter(
        item =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
          category.title.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter(category => category.items.length > 0);

  return (
    <>
      <SEO
        title="Help & Cooking Tips - FAQ"
        description="Need help with Cook with Otto? Find answers to FAQs about meal planning, recipe management, and AI cooking assistance. Get cooking tips, troubleshooting guides, and learn how to use features."
        url="/help"
        keywords={[
          'cooking help',
          'recipe app tutorial',
          'meal planning guide',
          'cooking tips',
          'AI cooking FAQ',
          'how to use cooking app',
        ]}
      />
      <BookContainer>
        <Box sx={{ width: '100%', py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 0,
            maxWidth: { xs: '100%', md: 1200 },
            margin: '0 auto',
          }}
        >
          {/* Left Page - Help Categories */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, minHeight: { xs: '50vh', md: 'auto' } }}>
            <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.HELP_LEFT}>
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 3, md: 4 },
                }}
              >
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 1,
                      fontSize: { xs: '1.75rem', md: '2.125rem' },
                    }}
                  >
                    Help & Support
                  </Typography>
                  <Divider
                    sx={{
                      mb: 3,
                      width: 100,
                      height: 2,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, rgba(255, 138, 101, 0.6), transparent)'
                        : 'linear-gradient(90deg, rgba(255, 107, 53, 0.6), transparent)',
                      border: 'none',
                    }}
                  />
                </motion.div>

                {/* Search Bar */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <TextField
                    fullWidth
                    placeholder="Search help topics..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{
                              color: theme.palette.mode === 'dark'
                                ? 'rgba(255, 248, 240, 0.5)'
                                : 'rgba(139, 69, 19, 0.5)',
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Crimson Text", serif',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.05)'
                          : 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </motion.div>

                {/* Accordion Categories */}
                <Box sx={{ maxHeight: { md: '60vh' }, overflowY: 'auto', pr: 1 }}>
                  {filteredCategories.map((category, index) => (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                    >
                      <Accordion
                        expanded={expanded === category.id}
                        onChange={handleAccordionChange(category.id)}
                        sx={{
                          mb: 2,
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.05)'
                            : 'rgba(255, 255, 255, 0.7)',
                          '&:before': {
                            display: 'none',
                          },
                          borderRadius: '8px !important',
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon
                              sx={{
                                color: theme.palette.mode === 'dark'
                                  ? 'rgba(255, 248, 240, 0.7)'
                                  : 'rgba(139, 69, 19, 0.7)',
                              }}
                            />
                          }
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {category.icon}
                            <Typography
                              sx={{
                                fontFamily: '"Playfair Display", serif',
                                fontWeight: 600,
                                color: theme.palette.mode === 'dark'
                                  ? 'rgba(255, 248, 240, 0.9)'
                                  : 'rgba(139, 69, 19, 0.9)',
                              }}
                            >
                              {category.title}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {category.items.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontFamily: '"Crimson Text", serif',
                                  fontWeight: 700,
                                  fontSize: '1.05rem',
                                  color: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 248, 240, 0.9)'
                                    : 'rgba(139, 69, 19, 0.9)',
                                  mb: 1,
                                }}
                              >
                                {item.question}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: '"Crimson Text", serif',
                                  fontSize: '0.95rem',
                                  color: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 248, 240, 0.7)'
                                    : 'rgba(139, 69, 19, 0.7)',
                                  lineHeight: 1.6,
                                }}
                              >
                                {item.answer}
                              </Typography>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </BookPage>
          </Box>

          {/* Right Page - Ask Otto */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, minHeight: { xs: '50vh', md: 'auto' } }}>
            <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.HELP_RIGHT}>
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 3, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  minHeight: { md: '70vh' },
                }}
              >
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                >
                  <Box
                    component="img"
                    src="/assets/images/otto/otto_help.svg"
                    alt="Otto"
                    sx={{
                      width: { xs: 140, md: 180 },
                      height: { xs: 140, md: 180 },
                      mb: 4,
                      filter: 'drop-shadow(4px 4px 8px rgba(0,0,0,0.2))',
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      fontStyle: 'italic',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 3,
                      fontSize: { xs: '1.75rem', md: '2rem' },
                    }}
                  >
                    When in doubt,
                    <br />
                    ask Otto to help you out!
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.7)'
                        : 'rgba(139, 69, 19, 0.7)',
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      mb: 4,
                      maxWidth: 400,
                    }}
                  >
                    Can't find what you're looking for? Otto is always here to help! You can ask
                    questions directly from any recipe page or while cooking.
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.6 }}
                >
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: '12px',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.1)'
                        : 'rgba(255, 107, 53, 0.1)',
                      border: `2px solid ${theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.3)'
                        : 'rgba(255, 107, 53, 0.3)'}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: '"Crimson Text", serif',
                        fontStyle: 'italic',
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.8)'
                          : 'rgba(139, 69, 19, 0.8)',
                        fontSize: '1rem',
                      }}
                    >
                      Look for the "Ask Otto for Help" button throughout the app, or simply
                      navigate to any recipe and start chatting with your AI sous chef!
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
            </BookPage>
          </Box>
        </Box>
      </Box>

      {/* Book Navigation */}
      <BookNavigation currentPageNumber={PAGE_NUMBERS.HELP_LEFT} />
    </BookContainer>
    </>
  );
};

export default HelpScreen;
